import { browserAssistantNotificationsEnabled } from "@/entities/assistant/assistant.completion-notifications"
import { getReview } from "@/entities/review/review.api"
import { getCatReviewRun } from "@/entities/translation/cat-review.api"
import {
  type CompletionAlert,
  completionKey,
  finishReviewCompletion,
  forgetReviewCompletion,
  isAwayFromCompletion,
  unreadReviewCompletions,
  watchedReviewCompletions,
} from "./completion-alerts"

const notifications = new Map<string, Notification>()

export const syncReviewBrowserNotifications = () => {
  const unread = new Set(unreadReviewCompletions().map(completionKey))
  for (const [key, notification] of notifications) {
    if (!unread.has(key)) {
      notification.close()
      notifications.delete(key)
    }
  }
}

export const notifyReviewCompletion = (
  entry: CompletionAlert,
  title: string,
  body: string,
  open: () => void
) => {
  if (
    !browserAssistantNotificationsEnabled() ||
    typeof Notification === "undefined" ||
    Notification.permission !== "granted" ||
    !isAwayFromCompletion(entry)
  )
    return false
  try {
    const key = completionKey(entry)
    notifications.get(key)?.close()
    const notification = new Notification(title, { body, tag: key })
    notifications.set(key, notification)
    notification.onclose = () => {
      if (notifications.get(key) === notification) notifications.delete(key)
    }
    notification.onclick = () => {
      notification.close()
      notifications.delete(key)
      if (unreadReviewCompletions().some((item) => completionKey(item) === key))
        open()
    }
    return true
  } catch {
    return false
  }
}

/** Poll saved outcomes even with the editor unmounted or the tab hidden. */
export const checkWatchedReviewCompletions = async () => {
  // Several languages share one review detail; read it once per pass.
  const reviews = new Map<string, ReturnType<typeof getReview>>()
  await Promise.all(
    watchedReviewCompletions().map(async (entry) => {
      try {
        let status: string | undefined
        if (entry.kind === "cat-review") {
          const run = await getCatReviewRun({
            jobId: entry.resourceId,
            runId: entry.runId,
          })
          if (!run || run.id !== entry.runId) {
            forgetReviewCompletion(entry)
            return
          }
          status = run.status
        } else {
          if (!reviews.has(entry.resourceId)) {
            reviews.set(
              entry.resourceId,
              getReview({ reviewId: entry.resourceId, quiet: true })
            )
          }
          const detail = await reviews.get(entry.resourceId)!
          const target = detail.targets.find((item) => item.id === entry.runId)
          if (!target) {
            forgetReviewCompletion(entry)
            return
          }
          // Ignore a stale cached outcome from before this target was claimed for evaluation.
          if (Date.parse(target.updateDt) < Date.parse(entry.startedAt)) return
          status = target.status
          if (status === "completed" && !target.conversationId) {
            forgetReviewCompletion(entry)
            return
          }
        }
        if (status === "completed")
          finishReviewCompletion(entry, isAwayFromCompletion(entry))
        else if (status === "failed") forgetReviewCompletion(entry)
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status
        if (status === 403 || status === 404) forgetReviewCompletion(entry)
        // Retry network and token-refresh failures; they do not establish an outcome.
      }
    })
  )
}
